import { ApplicationConfig, LOCALE_ID, APP_INITIALIZER, ErrorHandler, importProvidersFrom } from '@angular/core';
import { registerLocaleData, IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import localeEs from '@angular/common/locales/es';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideHttpClient, withInterceptorsFromDi, withFetch, HttpClient, HTTP_INTERCEPTORS } from '@angular/common/http';
import { provideRouter, withEnabledBlockingInitialNavigation } from '@angular/router';
import { provideClientHydration, withEventReplay, withHttpTransferCacheOptions } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';

import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { routes } from './app-routing.module';
import { DeferredErrorHandler } from './services/sentry.service';
import { TokenInterceptor } from './interceptors/token.interceptor';
import { RequestInterceptor } from './interceptors/request.interceptor';
import { ErrorsInterceptor } from './interceptors/errors.interceptor';
import { CountryService } from './services/country.service';
import { environment } from '../environments/environment';

registerLocaleData(localeEs, 'es-ES');

const imgproxyS3Bucket = environment.aws.staticUrl.match(/\/\/([^.]+)\./)?.[1] || 'glovostore-pre';

export function HttpLoaderFactory(http: HttpClient) {
  // Leading slash (not "./") so SsrApiBaseInterceptor rewrites the URL to
  // http://localhost:4000 during SSR. Without this, Angular resolved "./assets/i18n/en.json"
  // against the request URL (http://0.0.0.0/...), which is unreachable from inside the SSR
  // container and the translation request failed with ECONNREFUSED — leaving every
  // `| translate` pipe empty in the rendered HTML.
  return new TranslateHttpLoader(http, '/assets/i18n/', `.json?v=${environment.appVersion}`);
}

export function initializeCountries(countryService: CountryService) {
  return async () => {
    // Routes are pre-populated statically in app-routing.module.ts.
    // This initializer only loads full country data (currency, payment methods, etc.)
    const cachedCountries = countryService.loadCountriesWithCache();

    if (!cachedCountries) {
      await firstValueFrom(countryService.loadCountries());
    } else {
      setTimeout(() => {
        countryService.refreshCountriesInBackground();
      }, 0);
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(routes, withEnabledBlockingInitialNavigation()),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideClientHydration(
      withEventReplay(),
      withHttpTransferCacheOptions({
        filter: (req) => {
          // Don't cache auth-dependent API responses in TransferState.
          const authEndpoints = ['cart/check', 'customer/', 'order/', 'login'];
          if (authEndpoints.some(ep => req.url.includes(ep))) return false;

          // Exclude heavy payloads that aren't needed on initial render.
          // category/list (270KB) — only used when user opens catalog modal.
          // i18n/en.json (~47KB) — SSR loads both languages, client only needs one.
          const excludeFromTransferState = ['category/list', 'i18n/en.json'];
          if (excludeFromTransferState.some(ep => req.url.includes(ep))) return false;

          return true;
        },
      }),
    ),
    provideAnimations(),
    importProvidersFrom(TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient],
      },
    })),
    { provide: LOCALE_ID, useValue: 'es-ES' },
    // Sentry loads lazily via SentryService.init() + requestIdleCallback.
    // DeferredErrorHandler buffers errors until Sentry is ready, keeping 334KB off the critical path.
    { provide: ErrorHandler, useClass: DeferredErrorHandler },
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: RequestInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorsInterceptor, multi: true },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeCountries,
      deps: [CountryService],
      multi: true,
    },
    {
      provide: IMAGE_LOADER,
      useValue: (config: ImageLoaderConfig) => {
        let src = config.src || '';
        const idx = src.indexOf('/glovostore/');
        if (idx > 0) src = src.slice(idx);
        if (!src.startsWith('/glovostore/')) return config.src;
        const width = config.width;
        const resize = width ? `rs:fit:${width}:0/` : '';
        return `/imgproxy/unsafe/${resize}q:75/plain/s3://${imgproxyS3Bucket}${src}@webp`;
      },
    },
  ],
};
